import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { createInternalNoteSchema, type InternalNoteDto, type TripDto } from "@tc/shared";
import { Avatar, Button, Card, Icon, Textarea, Toast } from "@/components/ui";
import { Can } from "@/lib/auth/authz";
import { useFormatters } from "@/lib/i18n/format";
import { notesApi } from "./api";

/**
 * Internal notes tab (TW-3457) — TC-only annotations on a trip, amber-treated, author + timestamp.
 * This tab is mounted **only for back-office users** (see TripDetailPage), and the server enforces
 * the same with `@UserType("BACK_OFFICE")` + the `internal_note` RLS policy, so a club user can
 * never read or post a note even if the UI guard were bypassed.
 */
export function NotesTab({ trip }: { trip: TripDto }) {
  const { t } = useTranslation();
  const fmt = useFormatters();

  const [notes, setNotes] = useState<InternalNoteDto[] | null>(null);
  const [error, setError] = useState(false);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const [toast, setToast] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(false);
    try {
      setNotes(await notesApi.listNotes(trip.id));
    } catch {
      setError(true);
    }
  }, [trip.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const flash = (msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(null), 2400);
  };

  const post = async () => {
    const parsed = createInternalNoteSchema.safeParse({ body: draft });
    if (!parsed.success || posting) return;
    setPosting(true);
    try {
      const created = await notesApi.createNote(trip.id, parsed.data);
      setNotes((prev) => [created, ...(prev ?? [])]);
      setDraft("");
      flash(t("notes.posted"));
    } catch {
      flash(t("notes.loadError"));
    } finally {
      setPosting(false);
    }
  };

  return (
    <div data-fullw="notes">
      {toast && <Toast message={toast} />}

      <Can permission="internal_note.write">
        <div className="note-compose" style={{ marginBottom: 18 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
            <span className="internal-tag">{t("notes.internalOnly")}</span>
          </div>
          <Textarea
            rows={3}
            value={draft}
            placeholder={t("notes.placeholder")}
            onChange={setDraft}
            style={{ background: "#fff" }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
            <Button
              variant="primary"
              size="sm"
              icon="send"
              disabled={posting || draft.trim().length === 0}
              onClick={() => void post()}
            >
              {t("notes.post")}
            </Button>
          </div>
        </div>
      </Can>

      {error && <Card><div className="empty">{t("notes.loadError")}</div></Card>}

      {notes && notes.length === 0 && !error && (
        <Card>
          <div className="empty">
            <div className="empty-ic">
              <Icon name="doc" size={24} />
            </div>
            <div style={{ fontWeight: 600, marginTop: 4 }}>{t("notes.empty")}</div>
          </div>
        </Card>
      )}

      <div className="note-list">
        {notes?.map((n) => (
          <div className="note" key={n.id}>
            <Avatar initials={initials(n.authorName)} size={34} />
            <div className="note-body">
              <div className="note-top">
                <b>{n.authorName ?? t("audit.system")}</b>
                <span className="faint" style={{ fontSize: 11.5 }}>
                  {fmt.relative(n.createdAt)}
                </span>
              </div>
              <div className="note-text">{n.body}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/** Two-letter initials from a display name (falls back to "TC" for system/unknown authors). */
function initials(name: string | null): string {
  if (!name) return "TC";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "TC";
  const first = parts[0]?.[0] ?? "";
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "TC";
}
